import { useState, useEffect, useRef } from "react";
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { Box, Typography, colors } from '@mui/material';
import { Button, Select, MenuItem, FormControl, CircularProgress } from '@mui/material';

import axios from "axios";
import { toast } from 'react-toastify';

import "./HighLevelModal.css";

import Auth, { API_URL } from "../../../../utils/auth";

const HighLevelModal = ({ onClose }) => {

  const [manageLoading, setManageLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [ghlData, setGHLData] = useState(null);
  const [ghlUsers, setGHLUsers] = useState([]);
  const [syncStats, setSyncStats] = useState({ totalSyncedContacts: 0, lastSyncDate: null });
  const [syncStatsLoading, setSyncStatsLoading] = useState(false);
  const syncStatsRequestRef = useRef(null);
  const statusRequestRef = useRef(null);

  // State for notification toggles
  const DEFAULT_CONFIG = {
    contactSync: false,
    contactsSynced: 0,
    companySync: false,
    clientSync: false,
    engagementSync: false,
    emailTracking: false,
    meetingTracking: false,
    lastContactTracking: false,
    communicationImport: false,
    taskCreation: false,
    frequency: 'realtime'
  };

  const [ghlConfig, setGHLConfig] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    loadGHLSettings();
    loadSyncStats();
    
    const handleRefreshStats = () => {
      loadSyncStats();
    };
    
    window.addEventListener('refreshHighLevelStats', handleRefreshStats);
    
    return () => {
      if (statusRequestRef.current) {
        statusRequestRef.current = null;
      }
      if (syncStatsRequestRef.current) {
        syncStatsRequestRef.current = null;
      }
      window.removeEventListener('refreshHighLevelStats', handleRefreshStats);
    };
  }, []);


  const closeDialog = () => {
    setGHLData(null);
    setGHLUsers([]);
    setGHLConfig(null);
    setConfigLoaded(false);
    onClose();
  };

  const onManualSync = () => {
    onClose("manualSync");
  };

  const refreshSyncStats = () => {
    loadSyncStats();
  };

  const isEmpty = (obj) => {
    if (obj == null) return true;
    if (typeof obj === 'string' && obj.trim() === '') return true;
    if (typeof obj === 'string') return Object.keys(JSON.parse(obj)).length === 0;
    if (typeof obj !== 'object') return true;
    return Object.keys(obj).length === 0;
  }

  const loadGHLSettings = async () => {
    if (statusRequestRef.current) {
      return;
    }
    
    setManageLoading(true);
    //const API_URL = "http://localhost:4000"; // Replace with your actual API URL
    const request = axios
      .get(`${API_URL}/ghlcb/status`)
      .then((response) => {
        const ghlData = {
          botToken: response?.data?.data?.bot_token,
          locationId: response?.data?.data?.team_id,
          companyId: response?.data?.data?.team_name,
          userId: response?.data?.data?.team_icon,
        };
        
        setGHLData(ghlData);
        setGHLUsers([response.data.location]);
        
        const rawConfig = response?.data?.data?.config;
        let parsed = null;
        try {
          parsed = rawConfig ? (typeof rawConfig === 'string' ? JSON.parse(rawConfig) : rawConfig) : null;
        } catch (e) {
          parsed = null;
        }
        const finalConfig = !parsed || isEmpty(parsed) ? DEFAULT_CONFIG : { ...DEFAULT_CONFIG, ...parsed };
        finalConfig.frequency = 'realtime';
        setGHLConfig(finalConfig);
        setConfigLoaded(true);
      })
      .catch((err) => {
        toast.error(`Failed to fetch HighLevel integration info: ${err.message}`);
        setGHLConfig(DEFAULT_CONFIG);
        setConfigLoaded(true);
      })
      .finally(() => {
        setManageLoading(false);
        statusRequestRef.current = null;
      });
    
    statusRequestRef.current = request;
  };

  const loadSyncStats = () => {
    if (syncStatsRequestRef.current) {
      return;
    }
    
    //const API_URL = "http://localhost:4000";
    setSyncStatsLoading(true);
    
    const request = axios
      .get(`${API_URL}/ghlcb/sync-stats`)
      .then((response) => {
        setSyncStats(response.data);
      })
      .catch((err) => {
        console.error("Failed to load sync stats:", err.message);
        setSyncStats({ totalSyncedContacts: 0, lastSyncDate: null });
      })
      .finally(() => {
        setSyncStatsLoading(false);
        syncStatsRequestRef.current = null;
      });
    
    syncStatsRequestRef.current = request;
  };

  const handleToggle = (key, value) => setGHLConfig(prev => ({ ...prev, [key]: (value !== undefined ? value : !prev[key]) }));

  const updateGHLConfig = () => {
    //const API_URL = "http://localhost:4000"; // Replace with your actual API URL
    setUpdateLoading(true);
    
    const configToSave = {
      ...ghlConfig,
      frequency: 'realtime'
    };
    
    axios
      .post(
        `${API_URL}/ghlcb/config`,
        configToSave
      )
      .then((response) => {
        toast.success('HighLevel settings has been updated successfully!');
        closeDialog();
      })
      .catch((err) => {
        console.error("Failed to update HighLevel settings:", err.message);
        toast.error('Failed to update HighLevel settings!');
      })
      .finally(() => setUpdateLoading(false));
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog">
        <h2>Manage HighLevel Settings</h2>
        {ghlUsers && ghlUsers[0] ? (
          <>
            <div className="ghl-branding">
            <img
              src={ghlUsers[0].logoUrl}
              alt={ghlUsers[0].business.name}
              className="ghl-company-icon"
            />
            <div className="ghl-location-meta">
              <h3>{ghlUsers[0].business.name}</h3>
              <p>{ghlUsers[0].city}, {ghlUsers[0].country}</p>
            </div>
            </div>
            <div className="ghl-main-layout">
              <div className="ghl-left-panel">
                <div className="ghl-sync-stats">
                {syncStatsLoading ? (
                  <CircularProgress size={24} sx={{ color: '#805ad5' }} />
                ) : (
                  <Typography
                    sx={{
                      fontSize: '48px',
                      fontWeight: 'bold',
                      color: '#805ad5',
                      lineHeight: 1,
                      mb: 1
                    }}
                  >
                    {syncStats.totalSyncedContacts || 0}
                  </Typography>
                )}
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#333',
                    textAlign: 'center',
                    mb: 2,
                    lineHeight: 1.4
                  }}
                >
                Number of Actively Synced<br />Contacts in ClientBloom
                </Typography>
              <Button
                className="sync-button"
                  sx={{
                    backgroundColor: '#805ad5',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '10px 20px',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#6b46c1'
                    }
                  }}
                onClick={onManualSync}
                  variant="contained"
                size="large"
                  startIcon={<PlayArrowRoundedIcon sx={{ color: 'white' }} />}
                disabled={updateLoading}
              >
                  Click to Manually Sync
              </Button>
            </div>
          </div>
            <div className="ghl-right-panel">
          <div className="notification-section">
            <div className="section-control">
                  <h3>Contact, Company & Engagement</h3>
                </div>
            {!configLoaded || !ghlConfig ? (
              <div className="toggle-list" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                <CircularProgress size={20} />
            </div>
            ) : (
            <div className="toggle-list">
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={ghlConfig.contactSync ?? false}
                      onChange={(e) => handleToggle("contactSync")}
                    />
                    <span className="slider"></span>
                  </label>
                  <span className="toggle-title">Two-Way Contact Sync</span>
                </div>
                <p className="toggle-description-i">Notes and updates sync automatically between both platforms</p>
              </div>
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={ghlConfig.companySync ?? false}
                      onChange={(e) => handleToggle("companySync")}
                    />
                    <span className="slider"></span>
                  </label>
                  <span className="toggle-title">Company Data Sync</span>
                </div>
                <p className="toggle-description-i">Keep company info, size, and industry aligned</p>
              </div>
              {/* <div className="toggle-item">
                <div className="toggle-header-left">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={ghlConfig.clientSync ?? false}
                      onChange={(e) => handleToggle("clientSync")}
                    />
                    <span className="slider"></span>
                  </label>
                  <span className="toggle-title">Client Profile Sync</span>
                </div>
                <p className="toggle-description-i">New HighLevel contacts instantly create ClientBloom profiles</p>
              </div> */}
              <div className="toggle-item">
                <div className="toggle-header-left">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={ghlConfig?.engagementSync ?? false}
                      onChange={() => handleToggle("engagementSync")}
                    />
                    <span className="slider"></span>
                  </label>
                  <span className="toggle-title">Engagement Event Sync</span>
                </div>
                <p className="toggle-description-i">Sync email, calls, messages, appointments/meetings on schedule</p>
              </div>
            </div>
            )}
          </div>

          <div className="notification-section">
            <div className="section-control">
              <h3>Integration Settings</h3>
            </div>

            <div className="channel-selection">
              <h4>HighLevel Account Selection</h4>
              <FormControl sx={{ m: 0.5 }} size="small">
                <Select
                  className="channel-dropdown"
                  value={ghlUsers[0]?.id || ""}
                >
                  {ghlUsers.length > 0 ? (
                    ghlUsers.map((ghlUser) => (
                      <MenuItem className="channel-dropdown-item" key={ghlUser.id} value={ghlUser.id}>
                        <LocationCityIcon sx={{ width: '18px', marginX: '4px' }} /> {ghlUser.business.name} ({ghlUser.city}, {ghlUser.country})
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem className="channel-dropdown-item" value="" disabled>No Users available</MenuItem>
                  )}
                </Select>
              </FormControl>
              <p className="channel-description">Select which HighLevel account to link</p>
            </div>

          </div>
        </div>
            </div>
          </>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", margin: "20px 0", height: '90px' }}>
            <CircularProgress
              size={36}
              sx={{
                color: colors.blue[500],
                marginRight: 4,
              }}
            />
            <p>Loading HighLevel integration info...</p>
          </div>
        )}
        <div className="dialog-actions">
          <Box
            sx={{ m: 1, position: 'relative' }}>
            <Button
              className="update-button"
              sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
              onClick={updateGHLConfig}
              variant="outlined"
              color="success"
              size="large"
              disabled={updateLoading}
            >
              Update
            </Button>
            {updateLoading && (
              <CircularProgress
                size={24}
                sx={{
                  color: colors.green[500],
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
          <Box
            sx={{ m: 1, position: 'relative' }}>
            <Button
              className="close-button"
              sx={{ borderWidth: 2, borderRadius: '16px', padding: '10px 30px', fontSize: '16px', fontWeight: 'bold', '&:hover': { borderWidth: 2 } }}
              onClick={closeDialog}
              variant="outlined"
              color="error"
              size="large"
            >
              Close
            </Button>
          </Box>
        </div>
      </div>
    </div>
  )
};

export default HighLevelModal;